
import { useState } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import ChatWidget from '@/components/chat/ChatWidget';
import { ArrowLeft } from 'lucide-react';

const DemoPage = () => {
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [primaryColor, setPrimaryColor] = useState('#1A365D');
  const [secondaryColor, setSecondaryColor] = useState('#4299E1');
  const [position, setPosition] = useState<'bottom-right' | 'bottom-left'>('bottom-right');
  
  const hotelInfo = {
    name: 'Demo Hotel',
    description: 'A luxury hotel in the heart of the city.',
    checkInTime: '15:00',
    checkOutTime: '11:00',
    services: [
      'Free WiFi',
      'Swimming Pool',
      'Gym',
      'Spa',
      '24/7 Room Service',
      'Airport Shuttle'
    ],
    faqs: [
      {
        id: '1',
        question: 'What time is check-in?',
        answer: 'Check-in time is at 3:00 PM. Early check-in may be available upon request, subject to availability.'
      },
      {
        id: '2',
        question: 'Is breakfast included in the room rate?',
        answer: 'Yes, a complimentary breakfast buffet is included with all room bookings. Breakfast is served from 6:30 AM to 10:30 AM in our main restaurant.'
      },
      {
        id: '3',
        question: 'Do you offer airport transfers?',
        answer: 'Yes, we offer airport transfers for an additional fee. Please contact our concierge at least 24 hours in advance to arrange transportation.'
      }
    ]
  };
  
  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-12">
        <div className="flex items-center mb-8">
          <Link to="/" className="mr-4">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <h1 className="text-3xl font-bold text-hotel-primary">Chat Widget Demo</h1>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-semibold mb-4 text-hotel-primary">Demo Hotel Website</h2>
              
              <div className="prose max-w-none">
                <h3>Welcome to Demo Hotel</h3>
                <p>
                  Experience luxury and comfort at our premier hotel located in the heart of the city. 
                  With stunning views, world-class amenities, and exceptional service, your stay 
                  with us will be unforgettable.
                </p>
                
                <h4>Our Amenities</h4>
                <ul>
                  <li>Luxurious rooms and suites</li>
                  <li>Rooftop infinity pool with city views</li>
                  <li>Award-winning spa and wellness center</li>
                  <li>Fine dining restaurant and lounge bar</li>
                  <li>State-of-the-art fitness center</li>
                  <li>Conference and event spaces</li>
                </ul>
                
                <p>
                  Try asking our AI concierge about our check-in times, services, or 
                  anything else you'd like to know about our hotel!
                </p>
                
                <div className="mt-6">
                  <Button onClick={() => setIsCustomizing(!isCustomizing)}>
                    {isCustomizing ? 'Hide Customization' : 'Customize Widget'}
                  </Button>
                </div>
                
                {isCustomizing && (
                  <div className="mt-6 p-4 border rounded-md bg-gray-50">
                    <h4 className="text-lg font-semibold mb-4">Customize Chat Widget</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Primary Color
                        </label>
                        <div className="flex items-center space-x-2">
                          <input 
                            type="color" 
                            value={primaryColor}
                            onChange={(e) => setPrimaryColor(e.target.value)}
                            className="h-8 w-8 border rounded"
                          />
                          <input 
                            type="text" 
                            value={primaryColor}
                            onChange={(e) => setPrimaryColor(e.target.value)}
                            className="border rounded px-2 py-1 text-sm w-full"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Secondary Color
                        </label>
                        <div className="flex items-center space-x-2">
                          <input 
                            type="color" 
                            value={secondaryColor}
                            onChange={(e) => setSecondaryColor(e.target.value)}
                            className="h-8 w-8 border rounded"
                          />
                          <input 
                            type="text" 
                            value={secondaryColor}
                            onChange={(e) => setSecondaryColor(e.target.value)}
                            className="border rounded px-2 py-1 text-sm w-full"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Widget Position
                      </label>
                      <div className="flex space-x-4">
                        <label className="flex items-center space-x-2">
                          <input 
                            type="radio" 
                            name="position"
                            checked={position === 'bottom-right'}
                            onChange={() => setPosition('bottom-right')}
                          />
                          <span>Bottom Right</span>
                        </label>
                        
                        <label className="flex items-center space-x-2">
                          <input 
                            type="radio" 
                            name="position"
                            checked={position === 'bottom-left'}
                            onChange={() => setPosition('bottom-left')}
                          />
                          <span>Bottom Left</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-semibold mb-4 text-hotel-primary">About This Demo</h2>
              <p className="text-gray-600 mb-4">
                This demo simulates how our AI chat assistant would appear on your hotel website. 
                The chat responds with pre-set messages for demonstration purposes.
              </p>
              <p className="text-gray-600 mb-4">
                In the full version, the AI assistant uses GPT-4 to provide intelligent, 
                context-aware responses based on your hotel information.
              </p>
              <div className="mt-6">
                <Link to="/register">
                  <Button className="w-full bg-hotel-primary hover:bg-hotel-primary/90">
                    Get Started Free
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <ChatWidget 
        hotelInfo={hotelInfo} 
        position={position}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    </MainLayout>
  );
};

export default DemoPage;
